// Theme management — sets `data-theme="dark|light"` on <html>.
//
// Read once synchronously before React mounts to prevent flash-of-wrong-theme.
// Persisted in localStorage under `eid:theme`. Default: dark.

export type Theme = 'dark' | 'light';
const STORAGE_KEY = 'eid:theme';

export function readStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
  // Keep <meta name="theme-color"> in sync so iOS/Android chrome matches.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', theme === 'light' ? '#fafafa' : '#181c20');
  }
}

export function applyInitialTheme(): Theme {
  const t = readStoredTheme();
  applyTheme(t);
  return t;
}

function setStoredTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // ignore — private mode / quota
  }
  applyTheme(theme);
}

// Simple subscribe for components — uses a custom event so multiple toggles
// stay in sync without a Zustand store dependency for one boolean.
const EVENT = 'eid:theme-change';

export function toggleTheme(): Theme {
  const next: Theme = readStoredTheme() === 'dark' ? 'light' : 'dark';
  setStoredTheme(next);
  window.dispatchEvent(new CustomEvent<Theme>(EVENT, { detail: next }));
  return next;
}

export function subscribeTheme(cb: (t: Theme) => void): () => void {
  const handler = (e: Event) => cb((e as CustomEvent<Theme>).detail);
  window.addEventListener(EVENT, handler);
  return () => window.removeEventListener(EVENT, handler);
}
